import { useState, useRef, useEffect } from 'react';
import { sendChatMessage, extractProfile, getProviders, getLifestyleConfig, ChatMessage, AIProvider } from '../services/api';
import { getLifestyleProfile, isAuthenticated, getLifestyleToggle, setLifestyleToggle as persistLifestyleToggle } from '../services/authService';

interface ChatProps {
  onProfileComplete: (profile: any, messages: ChatMessage[], searchTitle?: string) => void;
}

interface LifestyleFactor {
  key: string;
  label_de: string;
  label_en: string;
  default_value: number;
}

interface LifestyleConfig {
  enabled: boolean;
  factors: LifestyleFactor[];
}

/**
 * Render lightweight markdown bold (**text**) as <strong> for assistant messages.
 * Only processes bold markers; all other text is treated as plain text (safe).
 */
function renderMarkdown(text: string) {
  // Split on **...** patterns, preserving the captured groups
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        // Odd indices are the bold captures, even indices are plain text
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
      )}
    </>
  );
}

export default function Chat({ onProfileComplete }: ChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showLifestyleSliders, setShowLifestyleSliders] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [availableProviders, setAvailableProviders] = useState<AIProvider[]>([]);
  const [userCanChoose, setUserCanChoose] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lifestyleConfig, setLifestyleConfig] = useState<LifestyleConfig | null>(null);
  const [lifestylePriorities, setLifestylePriorities] = useState<Record<string, number>>({});
  const [userLifestyleProfile, setUserLifestyleProfile] = useState<any>(null);
  const lifestyleProfileRef = useRef<any>(null);
  const [useLifestyleForSearch, setUseLifestyleForSearch] = useState<boolean>(true);
  const [lifestyleToggle, setLifestyleToggle] = useState<boolean>(getLifestyleToggle());
  const [initReady, setInitReady] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load available providers, lifestyle config, and user's lifestyle profile
  useEffect(() => {
    getProviders().then(data => {
      setAvailableProviders(data.providers);
      if (data.default) setProvider(data.default);
      if (data.userCanChoose !== undefined) setUserCanChoose(data.userCanChoose);
    }).catch(() => { /* ignore */ });

    // Load config defaults THEN overlay lifestyle profile values (sequential to avoid race condition)
    async function loadPriorities() {
      // Step 1: Load config defaults
      let priorities: Record<string, number> = {};
      try {
        const configData = await getLifestyleConfig();
        setLifestyleConfig(configData);
        configData.factors.forEach((f: LifestyleFactor) => {
          priorities[f.key] = f.default_value;
        });
      } catch { /* ignore */ }

      // Step 2: Load lifestyle profile and overlay soft priorities on top of defaults
      if (isAuthenticated()) {
        try {
          const data = await getLifestyleProfile();
          if (data.profile) {
            setUserLifestyleProfile(data.profile);
            lifestyleProfileRef.current = data.profile;
            if (data.profile.soft) {
              // Profile values always override config defaults
              priorities = { ...priorities, ...data.profile.soft };
            }
          }
        } catch { /* ignore */ }
      }

      // Step 3: Set final merged priorities in one call and signal ready
      setLifestylePriorities(priorities);
      setInitReady(true);
    }

    loadPriorities();
  }, []);

  // Auto-scroll to bottom within chat container (not the page) and focus input
  useEffect(() => {
    // Scroll only inside the chat messages container
    const el = chatEndRef.current;
    if (el?.parentElement) {
      el.parentElement.scrollTop = el.parentElement.scrollHeight;
    }
    // Focus input field after messages update (AI responded)
    if (!isLoading && !isComplete && !showLifestyleSliders) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isLoading]);

  // Start conversation only AFTER lifestyle profile has been loaded (or confirmed absent)
  useEffect(() => {
    if (initReady && messages.length === 0) {
      startConversation();
    }
  }, [initReady]);

  async function startConversation(useLifestyle?: boolean) {
    setIsLoading(true);
    setError(null);
    try {
      // Determine if we should send the lifestyle profile
      const shouldUseLifestyle = useLifestyle !== undefined ? useLifestyle : lifestyleToggle;
      const currentLifestyle = lifestyleProfileRef.current;
      const profileToSend = (shouldUseLifestyle && currentLifestyle) ? currentLifestyle : undefined;

      const firstMessage = profileToSend
        ? 'Hallo, ich bin zurück und möchte eine Immobiliensuche durchführen.'
        : 'Hallo, ich suche eine Immobilie in der Schweiz.';
      const result = await sendChatMessage(
        [{ role: 'user', content: firstMessage }],
        provider,
        profileToSend
      );
      setMessages([
        { role: 'user', content: firstMessage },
        { role: 'assistant', content: result.message },
      ]);
    } catch (err: any) {
      setError(err.message || 'Verbindungsfehler');
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }

  async function handleSend() {
    if (!input.trim() || isLoading || isComplete) return;

    const userMessage: ChatMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const profileForChat = (lifestyleToggle && userLifestyleProfile) ? userLifestyleProfile : undefined;
      const result = await sendChatMessage(updatedMessages, provider, profileForChat);
      const assistantMessage: ChatMessage = { role: 'assistant', content: result.message };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);

      // Detect if user agreed to use lifestyle profile (simple heuristic based on conversation)
      const userText = input.trim().toLowerCase();
      if (userLifestyleProfile && (
        userText.includes('ja') || userText.includes('weiter') || userText.includes('beibehalten') ||
        userText.includes('yes') || userText.includes('genau so') || userText.includes('passt')
      ) && messages.length <= 4) {
        // Early in conversation + affirmative response = likely agreeing to use profile
        setUseLifestyleForSearch(true);
      }
      if (userLifestyleProfile && (
        userText.includes('komplett neu') || userText.includes('neue suche') ||
        userText.includes('von vorne') || userText.includes('ganz neu') ||
        userText.includes('fresh start') || userText.includes('start over')
      ) && messages.length <= 4) {
        setUseLifestyleForSearch(false);
      }

      if (result.isComplete) {
        setIsComplete(true);
        // Show lifestyle sliders if enabled, otherwise auto-extract
        if (lifestyleConfig?.enabled) {
          setShowLifestyleSliders(true);
        } else {
          handleExtractProfile(finalMessages);
        }
      }
    } catch (err: any) {
      setError(err.message || 'AI-Fehler');
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }

  async function handleExtractProfile(chatMessages: ChatMessage[]) {
    setIsExtracting(true);
    setShowLifestyleSliders(false);
    try {
      const profileForExtract = (lifestyleToggle && userLifestyleProfile) ? userLifestyleProfile : undefined;
      const result = await extractProfile(
        chatMessages,
        provider,
        lifestylePriorities,
        profileForExtract,
        lifestyleToggle && useLifestyleForSearch
      );
      onProfileComplete(result.profile, chatMessages, result.searchTitle);
    } catch (err: any) {
      setError('Profilerfassung fehlgeschlagen: ' + (err.message || ''));
      setIsComplete(false);
    }
    setIsExtracting(false);
  }

  function handleToggleLifestyle() {
    const newValue = !lifestyleToggle;
    setLifestyleToggle(newValue);
    persistLifestyleToggle(newValue);
    // Restart conversation with or without the profile
    setMessages([]);
    setIsComplete(false);
    setShowLifestyleSliders(false);
    setIsExtracting(false);
    setError(null);
    setUseLifestyleForSearch(false);
    startConversation(newValue);
  }

  function handleLifestyleChange(key: string, value: number) {
    setLifestylePriorities(prev => ({ ...prev, [key]: value }));
  }

  function handleLifestyleSubmit() {
    handleExtractProfile(messages);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="chat-container">
      {/* Provider selector – only shown if admin allows user choice */}
      {userCanChoose && availableProviders.length > 1 && (
        <div className="chat-provider-selector">
          <label>AI Modell:</label>
          {availableProviders.map(p => (
            <button
              key={p}
              className={`provider-btn ${p === provider ? 'provider-btn--active' : ''}`}
              onClick={() => setProvider(p)}
              disabled={isLoading}
            >
              {p === 'openai' ? 'OpenAI' : 'Grok'}
            </button>
          ))}
        </div>
      )}

      {/* Lifestyle profile toggle – only shown when user has a profile */}
      {lifestyleProfileRef.current && (
        <div className="chat-lifestyle-toggle">
          <label className="lifestyle-toggle-label">
            <span className="lifestyle-toggle-text">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Lebensstil-Profil verwenden
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={lifestyleToggle}
              className={`toggle-switch ${lifestyleToggle ? 'toggle-switch--on' : ''}`}
              onClick={handleToggleLifestyle}
              disabled={isLoading}
            >
              <span className="toggle-switch-knob" />
            </button>
          </label>
        </div>
      )}

      {/* Messages */}
      <div className="chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
            {msg.role === 'assistant' && <div className="chat-avatar">S</div>}
            <div className="chat-content">
              {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="chat-bubble chat-bubble--assistant">
            <div className="chat-avatar">S</div>
            <div className="chat-content chat-typing">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}
        {isExtracting && (
          <div className="chat-status">
            Profil wird erstellt...
          </div>
        )}
        {error && (
          <div className="chat-error">
            {error}
          </div>
        )}
        
        {/* Lifestyle Sliders */}
        {showLifestyleSliders && lifestyleConfig && (
          <div className="lifestyle-sliders-container">
            <div className="lifestyle-sliders-card">
              <h3 className="lifestyle-title">Ihre Lifestyle-Prioritäten</h3>
              <p className="lifestyle-subtitle">
                Bitte bewerten Sie, wie wichtig Ihnen folgende Faktoren sind (0 = unwichtig, 5 = sehr wichtig)
              </p>
              
              <div className="lifestyle-sliders">
                {lifestyleConfig.factors.map(factor => (
                  <div key={factor.key} className="lifestyle-slider-item">
                    <div className="lifestyle-slider-header">
                      <label htmlFor={`slider-${factor.key}`}>{factor.label_de}</label>
                      <span className="lifestyle-slider-value">{lifestylePriorities[factor.key] || 0}</span>
                    </div>
                    <div className="lifestyle-slider-track">
                      <input
                        id={`slider-${factor.key}`}
                        type="range"
                        min="0"
                        max="5"
                        step="1"
                        value={lifestylePriorities[factor.key] || 0}
                        onChange={(e) => handleLifestyleChange(factor.key, parseInt(e.target.value, 10))}
                        className="lifestyle-slider"
                      />
                      <div className="lifestyle-slider-labels">
                        <span>0</span>
                        <span>1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>4</span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                className="lifestyle-submit-btn"
                onClick={handleLifestyleSubmit}
              >
                Weiter zur Immobiliensuche →
              </button>
            </div>
          </div>
        )}
        
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      {!isComplete && !showLifestyleSliders && (
        <div className="chat-input-area">
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder="Ihre Antwort eingeben..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
          >
            Senden
          </button>
        </div>
      )}
    </div>
  );
}
